"use client";

import { c as _c } from "react/compiler-runtime";
import { IconXboxX } from "@tabler/icons-react";
import { AnimatePresence, useReducedMotion } from "motion/react";
import * as m from "motion/react-m";
import type { RefObject } from "react";
import { NotificationCard } from "@/components/notifications/notification-card";
import { NotificationEmptyState } from "@/components/notifications/notification-empty-state";
import { NotificationListSkeleton } from "@/components/notifications/notification-list-skeleton";
import type { NotificationData } from "@domainstack/types";
import { ScrollArea } from "@domainstack/ui/scroll-area";
import { Spinner } from "@domainstack/ui/spinner";
interface NotificationListProps {
  notifications: NotificationData[];
  isLoading: boolean;
  isError: boolean;
  view: "inbox" | "archive";
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  loadMoreRef?: RefObject<HTMLDivElement | null>;
  scrollAreaRef?: RefObject<HTMLDivElement | null>;
  onNotificationClick?: (notification: NotificationData) => void;
  onClosePopover?: () => void;
}
export function NotificationList(t0) {
  const $ = _c(28);
  const {
    notifications,
    isLoading,
    isError,
    view,
    hasNextPage,
    isFetchingNextPage,
    loadMoreRef,
    scrollAreaRef,
    onNotificationClick,
    onClosePopover
  } = t0;
  const shouldReduceMotion = useReducedMotion();
  const t1 = !isLoading && !isError && notifications.length > 0 ? "divide-y" : undefined;
  let t2;
  if ($[0] !== isError || $[1] !== isLoading || $[2] !== notifications || $[3] !== onClosePopover || $[4] !== view) {
    t2 = isLoading ? <NotificationListSkeleton /> : isError ? <div role="alert" className="flex flex-col items-center justify-center gap-1.5 py-12 text-sm"><IconXboxX className="size-4" />Failed to load notifications</div> : notifications.length === 0 ? <NotificationEmptyState variant={view} onClosePopover={onClosePopover} /> : null;
    $[0] = isError;
    $[1] = isLoading;
    $[2] = notifications;
    $[3] = onClosePopover;
    $[4] = view;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== isError || $[7] !== isLoading || $[8] !== notifications || $[9] !== onNotificationClick || $[10] !== shouldReduceMotion) {
    t3 = !isLoading && !isError && notifications.map(notification => <m.div key={notification.id} layout={shouldReduceMotion ? false : "position"} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: shouldReduceMotion ? 0.1 : 0.2,
      ease: "easeInOut"
    }}><NotificationCard notification={notification} onClick={() => onNotificationClick?.(notification)} /></m.div>);
    $[6] = isError;
    $[7] = isLoading;
    $[8] = notifications;
    $[9] = onNotificationClick;
    $[10] = shouldReduceMotion;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t3) {
    t4 = <AnimatePresence mode="popLayout" initial={false}>{t3}</AnimatePresence>;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== hasNextPage || $[15] !== isError || $[16] !== isFetchingNextPage || $[17] !== isLoading || $[18] !== loadMoreRef) {
    t5 = !isLoading && !isError && hasNextPage && <div ref={loadMoreRef} className="flex justify-center py-4">{isFetchingNextPage && <Spinner className="size-5 text-muted-foreground" />}</div>;
    $[14] = hasNextPage;
    $[15] = isError;
    $[16] = isFetchingNextPage;
    $[17] = isLoading;
    $[18] = loadMoreRef;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t4 || $[23] !== t5) {
    t6 = <div className={t1}>{t2}{t4}{t5}</div>;
    $[20] = t1;
    $[21] = t2;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== scrollAreaRef || $[26] !== t6) {
    t7 = <ScrollArea scrollRef={scrollAreaRef} className="min-h-0 flex-1 bg-popover/10">{t6}</ScrollArea>;
    $[25] = scrollAreaRef;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
}